import type {
  EncounterCheckoutAppliedDiscount,
  EncounterCheckoutCharge,
  EncounterCheckoutCredit,
  EncounterCheckoutData,
  EncounterCheckoutDiscountOption,
  EncounterCheckoutPerLineDiscount,
  PatientDefaultDiscount,
} from 'features/Procedure/state/procedure-api.type'
import { DISCOUNT_CATEGORY } from 'features/Procedure/features/treatments/components/discount-select/discount-utils'

export type CheckoutDiscountScope = 'all' | 'products' | 'procedures'
export type CheckoutDiscountSelections = Record<number, string | null>
export type CheckoutCreditAmounts = Record<number, number>

export type CheckoutChargePreview = {
  charge: EncounterCheckoutCharge
  discountOption: EncounterCheckoutDiscountOption | null
  subtotal: number
  discountAmount: number
  tax: number
  subtotalAfterDiscount: number
  total: number
  appliedDiscountLabel: string | null
}

export type CheckoutCreditPreview = {
  credit: EncounterCheckoutCredit
  availableAmount: number
  appliedAmount: number
  remainingAfterApply: number
}

export type CheckoutPreviewTotals = {
  chargesSubtotal: number
  totalTax: number
  backendDiscounts: number
  previewDiscounts: number
  totalAdjustments: number
  previousPayments: number
  previousCredits: number
  previousGiftCards: number
  previousTotal: number
  originalBalance: number
  appliedCredits: number
  amountBeforeCredits: number
  amountDue: number
  outstanding: number
}

export type CheckoutPreview = {
  charges: CheckoutChargePreview[]
  credits: CheckoutCreditPreview[]
  totals: CheckoutPreviewTotals
}

export type EncounterCheckoutCreditPayload = {
  credit_id: number
  session_id: number | null
  gc_id: number | null
  gc_code: string | null
  amount: number
}

export type EncounterCheckoutDiscountPayload = {
  billing_id: number
  discount_code: string
  original_amount: number
  discount_amount: number
  final_amount: number
  service_desc: string
  provider_id: number
}

export type EncounterCheckoutPayload = {
  encounter_tax: number
  encounter_balance: number
  prepayment_amount: number
  credits_applied: EncounterCheckoutCreditPayload[]
  discounts_applied: EncounterCheckoutDiscountPayload[]
  total_discount: number
}

export const CHECKOUT_DISCOUNT_SCOPE_OPTIONS: {
  value: CheckoutDiscountScope
  label: string
}[] = [
  { value: 'all', label: 'Products & Procedures' },
  { value: 'products', label: 'Products' },
  { value: 'procedures', label: 'Procedures' },
]

export const NO_DISCOUNT_OPTION_ID = 'none'

export const mapDiscountCategoryToCheckoutScope = (
  patientDefault: PatientDefaultDiscount | null | undefined,
): CheckoutDiscountScope => {
  const categoryId = patientDefault?.discount_category?.option_id
  if (categoryId === DISCOUNT_CATEGORY.PRODUCTS_ONLY) return 'products'
  if (categoryId === DISCOUNT_CATEGORY.PROCEDURES_ONLY) return 'procedures'
  return 'all'
}

export const roundCurrency = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

const getDiscountOptionById = (
  discounts: EncounterCheckoutDiscountOption[],
  optionId?: string | null,
): EncounterCheckoutDiscountOption | null => {
  if (!optionId) return null
  return discounts.find((discount) => discount.option_id === optionId) ?? null
}

const isChargeInDiscountScope = (
  charge: EncounterCheckoutCharge,
  scope: CheckoutDiscountScope,
): boolean => {
  if (scope === 'all') return true
  if (scope === 'products') return charge.is_product
  return !charge.is_product
}

const normalizeAmount = (value: unknown): number => {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value.replace(/,/g, '').trim())
      : 0

  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    return 0
  }

  return Math.max(0, numericValue)
}

const isLegacyTaxCharge = (charge: EncounterCheckoutCharge): boolean =>
  charge.code.trim().toUpperCase() === 'S9999'

export const isChargeLocked = (charge: EncounterCheckoutCharge): boolean => charge.billed === 1

export const hasLockedBackendDiscounts = (data: EncounterCheckoutData): boolean =>
  Boolean(data.summary.has_partial_payment && normalizeAmount(data.summary.total_adjustments) > 0)

const stripTrailingPercentSuffix = (title: string): string =>
  title.replace(/\s*\([^)]+\)\s*$/, '').trim()

export const formatAppliedDiscountLabel = (perLine: EncounterCheckoutPerLineDiscount): string => {
  const cleanName = stripTrailingPercentSuffix(perLine.discount_title || '')
  const insideParens = perLine.discount_notes
    ? `${cleanName} / ${perLine.discount_notes}`
    : cleanName
  const amount = Number(perLine.amount).toFixed(2)
  return insideParens ? `-$${amount} (${insideParens})` : `-$${amount}`
}

const resolveAppliedOptionId = (
  applied: EncounterCheckoutAppliedDiscount,
  discountOptions: EncounterCheckoutDiscountOption[],
): string | null => {
  if (applied.discount_code) return applied.discount_code
  const match = discountOptions.find((opt) => opt.title === applied.discount_title)
  return match?.option_id ?? null
}

export const deriveAppliedDiscountSelections = ({
  charges,
  appliedDiscounts,
  discountOptions,
}: {
  charges: EncounterCheckoutCharge[]
  appliedDiscounts: EncounterCheckoutAppliedDiscount[]
  discountOptions: EncounterCheckoutDiscountOption[]
}): CheckoutDiscountSelections => {
  return charges.reduce<CheckoutDiscountSelections>((acc, charge) => {
    const perCharge = appliedDiscounts.find((a) => a.code && a.code === charge.code)
    if (perCharge) {
      acc[charge.billing_id] = resolveAppliedOptionId(perCharge, discountOptions)
      return acc
    }
    const global = appliedDiscounts.find((a) => !a.code)
    acc[charge.billing_id] = global ? resolveAppliedOptionId(global, discountOptions) : null
    return acc
  }, {})
}

export const deriveGlobalAppliedDiscountId = (
  appliedDiscounts: EncounterCheckoutAppliedDiscount[],
  discountOptions: EncounterCheckoutDiscountOption[],
): string | null => {
  if (appliedDiscounts.length === 0) return null
  const optionIds = appliedDiscounts
    .map((a) => resolveAppliedOptionId(a, discountOptions))
    .filter((id): id is string => Boolean(id))
  if (optionIds.length === 0) return null
  const first = optionIds[0]
  return optionIds.every((id) => id === first) ? first : null
}

export const applyGlobalDiscountToCharges = ({
  charges,
  optionId,
  scope,
}: {
  charges: EncounterCheckoutCharge[]
  optionId: string | null
  scope: CheckoutDiscountScope
}): CheckoutDiscountSelections => {
  return charges.reduce<CheckoutDiscountSelections>((acc, charge) => {
    acc[charge.billing_id] = optionId && isChargeInDiscountScope(charge, scope) ? optionId : null
    return acc
  }, {})
}

// Three-branch Global Discount inference driven by saved per-line billing state:
//   (1) No per-line discount  -> { kind: 'empty' }      caller applies demographics + cascade.
//   (2) Uniform per-line code -> { kind: 'uniform', code }  caller sets dropdown, NO cascade.
//   (3) Mixed per-line codes  -> { kind: 'mixed' }      caller shows "-----", NO cascade.
export type InferredGlobalDiscount =
  | { kind: 'empty' }
  | { kind: 'uniform'; code: string }
  | { kind: 'mixed' }

export const deriveInferredGlobalDiscount = (
  perLineDiscounts: Record<string, EncounterCheckoutPerLineDiscount> | undefined,
): InferredGlobalDiscount => {
  const codes = Object.values(perLineDiscounts ?? {})
    .map((entry) => entry?.discount_code)
    .filter((code): code is string => Boolean(code))

  if (codes.length === 0) return { kind: 'empty' }

  const unique = Array.from(new Set(codes))
  if (unique.length === 1) return { kind: 'uniform', code: unique[0] }
  return { kind: 'mixed' }
}

export const derivePerLineDiscountSelections = ({
  charges,
  perLineDiscounts,
  defaultOptionId,
  defaultScope,
}: {
  charges: EncounterCheckoutCharge[]
  perLineDiscounts: Record<string, EncounterCheckoutPerLineDiscount> | undefined
  defaultOptionId: string | null
  defaultScope: CheckoutDiscountScope
}): CheckoutDiscountSelections => {
  const perLine = perLineDiscounts ?? {}
  return charges.reduce<CheckoutDiscountSelections>((acc, charge) => {
    const entry = perLine[String(charge.billing_id)]
    if (entry?.discount_code) {
      acc[charge.billing_id] = entry.discount_code
      return acc
    }
    acc[charge.billing_id] =
      defaultOptionId && isChargeInDiscountScope(charge, defaultScope) ? defaultOptionId : null
    return acc
  }, {})
}

export const clampCreditAmounts = ({
  creditAmounts,
  credits,
  maxApplicableAmount,
}: {
  creditAmounts: CheckoutCreditAmounts
  credits: EncounterCheckoutCredit[]
  maxApplicableAmount: number
}): CheckoutCreditAmounts => {
  let remainingApplicableAmount = normalizeAmount(maxApplicableAmount)

  return credits.reduce<CheckoutCreditAmounts>((acc, credit) => {
    const requestedAmount = normalizeAmount(creditAmounts[credit.credit_id])
    const appliedAmount = Math.min(
      requestedAmount,
      normalizeAmount(credit.remaining_amount),
      remainingApplicableAmount,
    )

    acc[credit.credit_id] = roundCurrency(appliedAmount)
    remainingApplicableAmount = roundCurrency(remainingApplicableAmount - appliedAmount)
    return acc
  }, {})
}

export const calculateCheckoutPreview = ({
  data,
  discountSelections,
  creditAmounts,
}: {
  data: EncounterCheckoutData
  discountSelections: CheckoutDiscountSelections
  creditAmounts: CheckoutCreditAmounts
}): CheckoutPreview => {
  const charges = data.charges
    .filter((charge) => !isLegacyTaxCharge(charge))
    .map<CheckoutChargePreview>((charge) => {
      const rowLocked = isChargeLocked(charge)
      const billingKey = String(charge.billing_id)
      const perLine = data.has_per_line_discounts
        ? data.per_line_discounts?.[billingKey] ?? null
        : null

      const subtotal = normalizeAmount(charge.fee)

      const discountOption = rowLocked
        ? null
        : getDiscountOptionById(data.discounts, discountSelections[charge.billing_id])

      const useLockedPerLine = rowLocked && perLine !== null
      const discountAmount = useLockedPerLine
        ? roundCurrency(normalizeAmount(perLine!.amount))
        : discountOption
        ? Math.min(
            subtotal,
            roundCurrency((subtotal * normalizeAmount(discountOption.percent)) / 100),
          )
        : 0

      const subtotalAfterDiscount = useLockedPerLine
        ? roundCurrency(normalizeAmount(perLine!.final_amount))
        : roundCurrency(subtotal - discountAmount)

      // Locked rows: tax recomputed from final_amount × tax_rate (drift-free per row).
      // Unlocked rows: tax from the (possibly newly-discounted) line.
      const tax = roundCurrency(subtotalAfterDiscount * normalizeAmount(charge.tax_rate))

      const appliedDiscountLabel = rowLocked
        ? perLine !== null && normalizeAmount(perLine.amount) > 0
          ? formatAppliedDiscountLabel(perLine)
          : ''
        : null

      return {
        charge,
        discountOption,
        subtotal,
        discountAmount,
        tax,
        subtotalAfterDiscount,
        total: roundCurrency(subtotalAfterDiscount + tax),
        appliedDiscountLabel,
      }
    })
  const chargesSubtotal = roundCurrency(
    charges.reduce((total, item) => total + normalizeAmount(item.charge.fee), 0),
  )
  const calculatedTax = roundCurrency(charges.reduce((total, item) => total + item.tax, 0))

  const previewDiscounts = roundCurrency(
    charges.reduce((total, item) => total + item.discountAmount, 0),
  )
  const backendDiscounts = roundCurrency(normalizeAmount(data.summary.total_adjustments))
  const previousPayments = roundCurrency(normalizeAmount(data.summary.prev_payments))
  const previousCredits = roundCurrency(normalizeAmount(data.summary.prev_credits))
  const previousGiftCards = roundCurrency(normalizeAmount(data.summary.prev_gc))
  const previousBreakdownTotal = roundCurrency(
    previousPayments + previousCredits + previousGiftCards,
  )
  const previousTotal = roundCurrency(
    normalizeAmount(data.summary.total_payments) || previousBreakdownTotal,
  )

  // PER-LINE-AWARE DECISION TREE (mirrors PHP V46, single guard for all three totals):
  // - has_per_line_discounts === true → per-row sum is authoritative.
  //   Locked rows pulled their discount/tax from per_line_discounts already; unlocked
  //   rows came from the live projection. summary.total_adjustments is a legacy
  //   aggregate over applied_discounts that may include stale entries — DO NOT add it.
  //   saved_total_tax / saved_total_outstanding are point-in-time snapshots that go
  //   out of date as the user edits unlocked rows — DO NOT use them either.
  // - Legacy fully-billed (encounter_is_billed && !has_per_line_discounts):
  //   no per-row data exists, so use the saved snapshot fields as authoritative.
  // - New unbilled (no per-line, not billed): per-row projection only.
  const useSavedSnapshot = !data.has_per_line_discounts && Boolean(data.summary.encounter_is_billed)

  const totalAdjustments = useSavedSnapshot ? backendDiscounts : previewDiscounts

  const totalTax =
    useSavedSnapshot && normalizeAmount(data.saved_total_tax) > 0
      ? roundCurrency(normalizeAmount(data.saved_total_tax))
      : calculatedTax

  const originalBalance = roundCurrency(chargesSubtotal + totalTax - totalAdjustments)
  const amountBeforeCredits = roundCurrency(originalBalance - previousTotal)

  const clampedCreditAmounts = clampCreditAmounts({
    creditAmounts,
    credits: data.credits,
    maxApplicableAmount: Math.max(0, amountBeforeCredits),
  })
  const credits = data.credits.map<CheckoutCreditPreview>((credit) => {
    const availableAmount = normalizeAmount(credit.remaining_amount)
    const appliedAmount = clampedCreditAmounts[credit.credit_id] ?? 0
    return {
      credit,
      availableAmount,
      appliedAmount,
      remainingAfterApply: roundCurrency(availableAmount - appliedAmount),
    }
  })
  const appliedCredits = roundCurrency(
    credits.reduce((total, credit) => total + credit.appliedAmount, 0),
  )

  const outstanding =
    useSavedSnapshot && data.saved_total_outstanding !== undefined
      ? roundCurrency(data.saved_total_outstanding)
      : roundCurrency(amountBeforeCredits - appliedCredits)

  return {
    charges,
    credits,
    totals: {
      chargesSubtotal,
      totalTax,
      backendDiscounts,
      previewDiscounts,
      totalAdjustments,
      previousPayments,
      previousCredits,
      previousGiftCards,
      previousTotal,
      originalBalance,
      appliedCredits,
      amountBeforeCredits,
      amountDue: amountBeforeCredits,
      outstanding,
    },
  }
}

export const buildEncounterCheckoutPayload = (
  preview: CheckoutPreview,
): EncounterCheckoutPayload => {
  return {
    encounter_tax: roundCurrency(preview.totals.totalTax),
    encounter_balance: roundCurrency(preview.totals.amountBeforeCredits),
    prepayment_amount: 0,
    credits_applied: preview.credits
      .filter((item) => item.appliedAmount > 0)
      .map((item) => ({
        credit_id: item.credit.credit_id,
        session_id: item.credit.session_id,
        gc_id: item.credit.gc_id,
        gc_code: item.credit.gc_code,
        amount: roundCurrency(item.appliedAmount),
      })),
    discounts_applied: preview.charges
      .filter((item) => !isChargeLocked(item.charge))
      .filter((item) => item.discountOption && item.discountAmount > 0)
      .map((item) => ({
        billing_id: item.charge.billing_id,
        discount_code: item.discountOption?.option_id ?? '',
        original_amount: roundCurrency(item.subtotal),
        discount_amount: roundCurrency(item.discountAmount),
        final_amount: roundCurrency(item.subtotalAfterDiscount),
        service_desc: `${item.charge.code} - ${item.charge.description}`,
        provider_id: item.charge.provider_id || 0,
      })),
    total_discount: roundCurrency(preview.totals.totalAdjustments),
  }
}
